import {
  BufferGeometry,
  DirectionalLight,
  InstancedBufferAttribute,
  Material,
  Matrix4,
  MeshStandardMaterial,
  SRGBColorSpace,
  Texture,
  TextureLoader,
  Vector2,
  Vector3,
  WebGLProgramParametersWithUniforms,
  WebGLRenderer,
} from 'three';
import { InstancedMesh } from 'three';
import { MeshUserData } from '../helpers/Utility';

export const enum PanelTextureEnum {
  Normal = 0,
  Transparent = 1,
  Highlight = 2,
  Grey = 3,
  Hover = 4,
  GreyScale = 5,
  Shaded = 6,
  Red = 7,
  Yellow = 8,
}
class InstancedMeshUtil extends InstancedMesh {
  static textures: any;

  static loadPanelTextures(){
    InstancedMeshUtil.textures = {
      // type: 'tv',
      value: [
        new TextureLoader().load(
          './assets/threejs/Solar-panel-texture.png',
          (texture) => {
            texture.center = new Vector2(0.5, 0.5);
            texture.colorSpace = SRGBColorSpace;
          }
        ),
        new TextureLoader().load(
          './assets/threejs/Solar_panel-transparent-texture.png',
          (texture) => {
            texture.center = new Vector2(0.5, 0.5);
            texture.colorSpace = SRGBColorSpace;
          }
        ),
        new TextureLoader().load(
          './assets/threejs/highlight_solar_panel.png',
          (texture) => {
            texture.center = new Vector2(0.5, 0.5);
            texture.colorSpace = SRGBColorSpace;
          }
        ),
        new TextureLoader().load(
          './assets/threejs/grey_panel.png',
          (texture) => {
            texture.center = new Vector2(0.5, 0.5);
            texture.colorSpace = SRGBColorSpace;
          }
        ),
        new TextureLoader().load(
          './assets/threejs/panel_hover.png',
          (texture) => {
            texture.center = new Vector2(0.5, 0.5);
            texture.colorSpace = SRGBColorSpace;
          }
        ),
        new TextureLoader().load(
          './assets/threejs/color_panel_grey2.png',
          (texture) => {
            texture.center = new Vector2(0.5, 0.5);
            texture.colorSpace = SRGBColorSpace;
          }
        ),
        new TextureLoader().load(
          './assets/threejs/Shade_panel.png',
          (texture) => {
            texture.center = new Vector2(0.5, 0.5);
            texture.colorSpace = SRGBColorSpace;
          }
        ),
        new TextureLoader().load(
          './assets/threejs/Red.png',
          (texture) => {
            texture.center = new Vector2(0.5, 0.5);
            texture.colorSpace = SRGBColorSpace;
          }
        ),
        new TextureLoader().load(
          './assets/threejs/Yellow.png',
          (texture) => {
            texture.center = new Vector2(0.5, 0.5);
            texture.colorSpace = SRGBColorSpace;
          }
        ),
      ],
    } ;

  }

  static init(instancedMesh: InstancedMesh){
    InstancedMeshUtil.setGeomAttr(instancedMesh);
  }

  static setGeomAttr(instancedMesh: InstancedMesh) {
    // Add a directional light to the scene
  
    // Create a visibility attribute for each instance, default to visible (1)
    const visibility = new Float32Array(instancedMesh.count).fill(1); // 1 = visible, 0 = invisible
    const textures = new Float32Array(instancedMesh.count).fill(PanelTextureEnum.Normal); // 1 = visible, 0 = invisible
    const greyScale = new Float32Array(instancedMesh.count).fill(99);
    const shaded = new Float32Array(instancedMesh.count).fill(0);


    instancedMesh.geometry.setAttribute(
      'instanceVisibility',
      new InstancedBufferAttribute(visibility, 1)
    );
    instancedMesh.geometry.setAttribute(
      'textureIndex',
      new InstancedBufferAttribute(new Float32Array(textures), 1)
    );
    instancedMesh.geometry.setAttribute(
      'greyScale',
      new InstancedBufferAttribute(new Float32Array(greyScale), 1)
    );
    instancedMesh.geometry.setAttribute(
      'shaded',
      new InstancedBufferAttribute(new Float32Array(shaded), 1)
    );
  }

    // Global opacity uniform for panel translucency during obstacle/walkway mode
    static globalPanelOpacity = { value: 1.0 };
    
    // Track all panel materials for opacity updates
    private static panelMaterials: Set<MeshStandardMaterial> = new Set();
    
    // Store shader uniform references for direct updates
    private static shaderUniforms: WeakMap<MeshStandardMaterial, any> = new WeakMap();

    /**
     * Set the global panel opacity for all instanced panel meshes.
     * @param opacity - Opacity value (0.0 to 1.0)
     */
    static setGlobalPanelOpacity(opacity: number): void {
        const clampedOpacity = Math.max(0, Math.min(1, opacity));
        InstancedMeshUtil.globalPanelOpacity.value = clampedOpacity;
        
        // Also update the material opacity directly as a fallback
        InstancedMeshUtil.panelMaterials.forEach(material => {
            material.opacity = clampedOpacity;
            material.needsUpdate = true;
        });
    }

    static setPanelShader(instancedMesh: InstancedMesh) {
    const material = instancedMesh.material as MeshStandardMaterial;
    material.transparent = true;
    material.depthWrite = true;
    material.opacity = InstancedMeshUtil.globalPanelOpacity.value;
    
    // Track this material for opacity updates
    InstancedMeshUtil.panelMaterials.add(material);
    
    material.onBeforeCompile = (parameters: WebGLProgramParametersWithUniforms, renderer: WebGLRenderer) => {
  
      let mapFrag = `col = texture2D(textures[0], vMapUv ) * step(-0.1, x) * step(x, 0.1);`;
      for (let i = 0; i < this.textures.value.length; i++) {
        if (i === 5 || i === 6) {
          mapFrag += `col += texture2D(textures[${i}], vMapUv) * greyScaleColor * step(${i - 1}.9, x) * step(x, ${i}.1);`;
        } else {
          mapFrag += `col += texture2D(textures[${i}], vMapUv ) * step(${i - 1}.9, x) * step(x, ${i}.1);`;
        }
      }
  
      parameters.uniforms.textures = InstancedMeshUtil.textures;
      parameters.uniforms.lightDirection = { value: new Vector3() };
      parameters.uniforms.shadowIntensity = { value: 1.0 };
      parameters.uniforms.globalPanelOpacity = InstancedMeshUtil.globalPanelOpacity;
  
      parameters.vertexShader =
        `attribute float instanceVisibility;
        attribute float shaded;
        varying float vVisibility;
        varying float vShaded;
        varying vec3 vPosition;
        ${parameters.vertexShader}`
          .replace(
            `#include <begin_vertex>`,
            `#include <begin_vertex>
            vVisibility = instanceVisibility;
            vShaded = shaded;
            vPosition = (modelViewMatrix * vec4(position, 1.0)).xyz;`
          )
          .replace(
            '#define STANDARD',
            `#define STANDARD
            varying vec3 vTint;
            varying float vGreyScale;
            varying float vTextureIndex;`
          )
          .replace(
            '#include <common>',
            `#include <common>
            attribute vec3 tint;
            attribute float textureIndex;
            attribute float greyScale;`
          )
          .replace(
            '#include <project_vertex>',
            `#include <project_vertex>
            vTint = tint;
            vGreyScale = greyScale;
            vTextureIndex = textureIndex;`
          );
  
      parameters.fragmentShader = `
        uniform vec3 lightDirection;
        uniform float shadowIntensity;
        uniform float globalPanelOpacity;
        varying float vVisibility;
        varying float vShaded;
        varying vec3 vPosition;
        ${parameters.fragmentShader}
        `
        .replace(
          `#include <dithering_fragment>`,
          `if (vVisibility < 0.5) discard;
          gl_FragColor.a *= globalPanelOpacity;
          #include <dithering_fragment>`
        )
        .replace(
          '#define STANDARD',
          `#define STANDARD
          uniform sampler2D textures[${InstancedMeshUtil.textures.value.length}];
          varying vec3 vTint;
          varying float vTextureIndex;
          varying float vGreyScale;
  
        const int GRADIENT_STEPS = 5;
        vec3 gradient[GRADIENT_STEPS] = vec3[](
            vec3(1.0),        // white
            vec3(0.75),       // light gray
            vec3(0.5),        // medium gray
            vec3(0.25),       // dark gray
            vec3(0.01)         // black
        );

        vec4 greyScaleToRGBA(float greyScale) {
            float t = clamp(greyScale / 100.0, 0.0, 1.0);
            float fIndex = t * float(GRADIENT_STEPS - 1);
            int index = int(floor(fIndex));
            float frac = fract(fIndex);
            vec3 color = mix(gradient[index], gradient[min(index + 1, GRADIENT_STEPS - 1)], frac);
            return vec4(color, 1.0);
        }`
          )
          .replace(
            '#include <map_fragment>',
            `
            int texIdx = int(vTextureIndex);
            vec4 greyScaleColor = greyScaleToRGBA(vGreyScale);
            float x = vTextureIndex;
            vec4 col;
            ${mapFrag}
            diffuseColor *= col;
            `        );
      };
    }

  static setShader(instancedMesh: InstancedMesh) {
    const mat = instancedMesh.material as MeshStandardMaterial;
    mat.transparent = false;
    mat.opacity = 1;
    mat.depthWrite = true;
    mat.needsUpdate = true;

    mat.onBeforeCompile = (parameters: WebGLProgramParametersWithUniforms, renderer: WebGLRenderer) => {

      parameters.vertexShader =
        `attribute float instanceVisibility;
          varying float vVisibility;
          ${parameters.vertexShader}`
          .replace(
            `#include <begin_vertex>`,
            `#include <begin_vertex>
               vVisibility = instanceVisibility;`
          )
          .replace(
            '#define STANDARD',
            `#define STANDARD
               varying vec3 vTint;`
          )
          .replace(
            '#include <common>',
            `#include <common>
              attribute vec3 tint;`
          )
          .replace(
            '#include <project_vertex>',
            `#include <project_vertex>
    vTint = tint;`
          );

      parameters.fragmentShader = `
                    varying float vVisibility;
                    ${parameters.fragmentShader}
                `
        .replace(
          `#include <dithering_fragment>`,
          `if (vVisibility < 0.5) discard;
                    #include <dithering_fragment>
                    `
        )
        .replace(
          '#define STANDARD',
          `#define STANDARD
                          varying vec3 vTint;`
        )
    };
  }

  getGeometryAt(instancedMesh: InstancedMesh, index: number){
  }

  /**
   * Sets the visibility of a specific instance.
   * @param {number} index - The index of the instance.
   * @param {boolean} isVisible - True to make the instance visible, false to hide it.
   */
  static getVisibilityAt(instancedMesh: InstancedMesh, index: number) {
    const visibilityArray = (
      instancedMesh.geometry.attributes
        .instanceVisibility as InstancedBufferAttribute
    ).array;
    const visibility = new Float32Array(visibilityArray.length);
    visibility.set(visibilityArray);
    return visibility[index] == 1 ? true : false;
  }

  static getVisibilityArray(instancedMesh: InstancedMesh) {
    const visibilityArray = (
      instancedMesh.geometry.attributes
        .instanceVisibility as InstancedBufferAttribute
    ).array;
    const visibility = new Float32Array(visibilityArray.length);
    visibility.set(visibilityArray);
    return visibility;
  }
  /**
   * Sets the visibility of a specific instance.
   * @param {number} index - The index of the instance.
   * @param {boolean} isVisible - True to make the instance visible, false to hide it.
   */
  static setVisibilityAt(
    instancedMesh: InstancedMesh,
    index: number,
    isVisible: boolean
  ) {
    const visibilityArray = (
      instancedMesh.geometry.attributes
        .instanceVisibility as InstancedBufferAttribute
    ).array;
    const visibility = new Float32Array(visibilityArray.length);
    visibility.set(visibilityArray);
    visibility[index] = isVisible ? 1 : 0;
    instancedMesh.geometry.setAttribute(
      'instanceVisibility',
      new InstancedBufferAttribute(visibility, 1)
    );
    instancedMesh.geometry.attributes.instanceVisibility.needsUpdate = true;
    instancedMesh.userData.visibility = visibility;
  }

  /**
   * Sets the shaded attribute of a specific instance.
   * @param {number} index - The index of the instance.
   * @param {boolean} shadeValue - True to enable shading for the instance, false to disable it.
   */
  static setShadedAt(
    instancedMesh: InstancedMesh,
    index: number,
    shadeValue: number
  ) {
    const shadedArray = (
      instancedMesh.geometry.attributes
        .greyScale as InstancedBufferAttribute
    ).array;
    const shaded = new Float32Array(shadedArray.length);
    shaded.set(shadedArray);
    shaded[index] = shadeValue ? shadeValue : 0;
    instancedMesh.geometry.setAttribute(
      'greyScale',
      new InstancedBufferAttribute(shaded, 1)
    );
    instancedMesh.geometry.attributes.greyScale.needsUpdate = true;
    // instancedMesh.userData.greyScale = shaded;
  }


  /**
   * Sets the visibility of multiple instances at once.
   * @param {Array<number>} indices - Array of indices of the instances.
   * @param {boolean} isVisible - True to make the instances visible, false to hide them.
   */
  static setMultipleVisibilities(
    instancedMesh: InstancedMesh,
    indices: Array<number>,
    isVisible: boolean,
    setAll?: Boolean
  ) {
    if (!instancedMesh || !instancedMesh.geometry) {
      console.error('InstancedMesh or its geometry is not defined');
      return;
    }

    const visibilityArray = (
      instancedMesh.geometry.attributes.instanceVisibility as InstancedBufferAttribute
    ).array;
    const visibility = new Float32Array(visibilityArray.length);
    visibility.set(visibilityArray);
    const visibilityValue = isVisible ? 1 : 0;

    if (setAll) {
      for (let i = 0; i < visibility.length; i++) {
        visibility[i] = visibilityValue;
      }
    } else {
      indices.forEach((index) => {
        visibility[index] = visibilityValue;
      });
    }

    instancedMesh.geometry.setAttribute(
      'instanceVisibility',
      new InstancedBufferAttribute(visibility, 1)
    );
    instancedMesh.geometry.attributes.instanceVisibility.needsUpdate = true;
    instancedMesh.userData.visibility = visibility;
  }

   /**
   * Sets the visibility of a specific instance.
   * @param {number} index - The index of the instance.
   * @param {boolean} isVisible - True to make the instance visible, false to hide it.
   */
   static setTextureAt(
    instancedMesh: InstancedMesh,
    index: number,
    panelTexture: PanelTextureEnum
  ) {
    const textureArray = (
      instancedMesh.geometry.attributes
        .textureIndex as InstancedBufferAttribute
    ).array;
    const texture = new Float32Array(textureArray.length);
    texture.set(textureArray);
    texture[index] = panelTexture;
    instancedMesh.geometry.setAttribute(
      'textureIndex',
      new InstancedBufferAttribute(texture, 1)
    );
    instancedMesh.geometry.attributes.textureIndex.needsUpdate = true;
    instancedMesh.userData.texture = texture;
  }

  static getTextureAt(instancedMesh: InstancedMesh, index: number): PanelTextureEnum | undefined {
    // Get the texture index array from the geometry attributes
    const textureArray = (instancedMesh.geometry.attributes.textureIndex as InstancedBufferAttribute).array;

    // Check if the index is within bounds of the texture array
    if (index < 0 || index >= textureArray.length) {
        console.error(`Index ${index} out of bounds for texture array`);
        return undefined;
    }

    // Retrieve the texture value at the specified index
    const textures = new Float32Array(textureArray.length);
    textures.set(textureArray);
    return textures[index];
}


  /**
   * Sets the visibility of multiple instances at once.
   * @param {Array<number>} indices - Array of indices of the instances.
   * @param {boolean} isVisible - True to make the instances visible, false to hide them.
   */
  static setMultipleTextures(
    instancedMesh: InstancedMesh,
    indices: Array<number>,
    panelTexture: PanelTextureEnum,
    setAll?: Boolean
  ) {
    const textureArray = (
      instancedMesh.geometry.attributes
        .textureIndex as InstancedBufferAttribute
    ).array;
    const texture = new Float32Array(textureArray.length);
    texture.set(textureArray);

    const textureValue = panelTexture;

    if (setAll) {
      for (let i = 0; i < texture.length; i++) {
        texture[i] = textureValue;
      }
    } else {
      // Update texture for each index in the array
      indices.forEach((index) => {
        texture[index] = textureValue;
      });
    }

    instancedMesh.geometry.setAttribute(
      'textureIndex',
      new InstancedBufferAttribute(texture, 1)
    );
    instancedMesh.geometry.attributes.textureIndex.needsUpdate = true;
    instancedMesh.userData.texture = texture;
  }

  static addInstance(instancedMesh: InstancedMesh, matrix: Matrix4){
    instancedMesh.count+=1;
    instancedMesh.setMatrixAt(instancedMesh.count-1, matrix);
    InstancedMeshUtil.setVisibilityAt(instancedMesh, instancedMesh.count-1, true);
    instancedMesh.instanceMatrix.needsUpdate = true;
    instancedMesh.computeBoundingSphere();
  }

  /**
   * Sets the visibility of a specific instance.
   * @param {number} id - The id of the instance.
   * @param {boolean} isVisible - True to make the instance visible, false to hide it.
   */
  static delete(instancedMesh: InstancedMesh, id: number) {
    const lastInd = instancedMesh.count - 1;
    const lastMatrix = new Matrix4();
    const curMatrix = new Matrix4();
    instancedMesh.getMatrixAt(lastInd, lastMatrix);
    instancedMesh.getMatrixAt(id, curMatrix);

    instancedMesh.setMatrixAt(lastInd, curMatrix);
    instancedMesh.setMatrixAt(id, lastMatrix);

    const visiblity = InstancedMeshUtil.getVisibilityAt(instancedMesh, lastInd);
    InstancedMeshUtil.setVisibilityAt(instancedMesh, id, visiblity);
    const texture = InstancedMeshUtil.getTextureAt(instancedMesh, lastInd);
    InstancedMeshUtil.setTextureAt(instancedMesh, id, texture ?? PanelTextureEnum.Normal);

    instancedMesh.count -= 1;
    instancedMesh.instanceMatrix.needsUpdate = true;
    instancedMesh.computeBoundingSphere();

  }

  static restoreInstanceMesh(instancedMesh: InstancedMesh){
    const maxInstanceCount = (instancedMesh.userData as MeshUserData)
      .maxInstanceCount;
    const currentCount = instancedMesh.count;
    instancedMesh.count = maxInstanceCount ?? instancedMesh.count;
    InstancedMeshUtil.init(instancedMesh);
    if (instancedMesh.userData.visibility) {
      const parsedVisb = instancedMesh.userData.visibility;
      const values = Object.values(parsedVisb);
      const visibility = new Float32Array(values.length);
      visibility.set(values as ArrayLike<number>);
      instancedMesh.geometry.setAttribute(
        'instanceVisibility',
        new InstancedBufferAttribute(visibility, 1)
      );
    }
    // Always restore count to prevent extra instances from rendering
    instancedMesh.count = currentCount;
  }
}

export { InstancedMeshUtil };
